/* Employee account setup — accept invite token, set password, then sign in. */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmployeeSetup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [invite, setInvite] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/employee-auth/invite/${token}`)
      .then((r) => { setInvite(r.data); setState("ready"); })
      .catch((e) => { setState("invalid"); toast.error(e?.response?.data?.detail || "Invalid link"); });
  }, [token]);

  const submit = async () => {
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      await axios.post(`${API}/api/employee-auth/setup`, { token, password: pw, name: invite?.name });
      const login = await axios.post(`${API}/api/auth/login`, { email: invite.email, password: pw }, { withCredentials: true });
      toast.success("Account activated");
      if ((login.data?.role || "") === "sales") navigate("/sales", { replace: true });
      else navigate("/app", { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Setup failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#f9fafb] px-4" data-testid="employee-setup-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#e5e7eb] p-8">
        <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-10 mx-auto" />
        {state === "loading" && <div className="mt-8 flex justify-center"><Loader2 className="animate-spin text-[#9ca3af]" /></div>}
        {state === "invalid" && (
          <p className="mt-6 text-center text-sm text-[#6b7280]" data-testid="setup-invalid">
            This setup link is invalid or expired. Please ask your admin to resend your welcome email.
          </p>
        )}
        {state === "ready" && (
          <div className="mt-6">
            <h1 className="text-xl font-bold text-[#111827] text-center">Welcome{invite?.name ? `, ${invite.name.split(" ")[0]}` : ""}</h1>
            <p className="text-[13px] text-[#6b7280] text-center mt-1">Set a password for <b>{invite?.email}</b> to access your Sales Portal.</p>
            <div className="mt-5 space-y-3">
              <div>
                <Label className="text-xs">New password</Label>
                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" data-testid="setup-password" />
              </div>
              <div>
                <Label className="text-xs">Confirm password</Label>
                <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1" data-testid="setup-password-confirm" />
              </div>
              <Button onClick={submit} disabled={saving} className="w-full bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="setup-submit">
                {saving ? "Activating…" : "Activate account"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
